/**
 * @file Foot Facts loader — the "Live" data path (decision D9).
 *
 * Turns a Volumental scan id into a computed footFacts object, entirely client-side:
 *
 *   scanId → Volumental GraphQL fetch (measurement in metres + OBJ mesh URLs)
 *          → arch extraction from the meshes (window.ArchHeightCalculator)
 *          → measurements in mm → window.FootFactsCalculator.computeFootFacts
 *
 * The URL/entry point carries only the scan id; everything else is re-fetched and
 * recomputed on load, so the report always reflects the latest transformation logic.
 * Cold re-fetch by id was validated live on 2026-07-03 (see WEB-PORT-OUTLINE.md, D9,
 * in the foot-facts-email repo).
 *
 * Accepted ids:
 *   - a full internal id  — base64("<ScanType>:" + publicID), used as-is
 *   - a bare publicID     — a UUID (contains dashes); its scan type is ambiguous, so
 *     WebScan is tried first with a RetailScan fallback (Costco/roadshow scans)
 *
 * Load order: foot-facts-calculator.js and arch-height-calculator.js must be loaded
 * before calling load().
 *
 * Exposed as window.FootFactsLoader (same pattern as the other calculator assets).
 */
(function () {
  'use strict';

  // Guard against multiple loads.
  if (window.FootFactsLoader) return;

  // Public widget client-id — the same value volumental-widget.liquid embeds as its
  // x-visa-token (settings.volumental_client_id default). Callers should pass the
  // theme setting through options.visaToken; this is the fallback.
  const DEFAULT_VISA_TOKEN = '8906be05-6a77-479f-827c-f8d4f30e0336';
  const QUERY_URL = 'https://widget.volumental.com/query';
  const SCAN_TYPES = ['WebScan', 'RetailScan'];

  const QUERY = `query getMeasurementResultQuery($scanID: ID!) {
    scan(input: {scanID: $scanID}) {
      id
      publicID
      createdAt
      measurement {
        leftLength leftWidth leftInstepHeight
        rightLength rightWidth rightInstepHeight
      }
      meshes { leftFoot rightFoot }
    }
  }`;

  function encodeScanID(publicID, scanType) {
    // Volumental's internal id is a Relay global id; the trailing base64 padding is
    // stripped in the ids it returns, so strip it here too.
    return btoa(scanType + ':' + publicID).replace(/=+$/, '');
  }

  // Errors carry a machine-readable `code` so the report UI can map them to friendly
  // customer copy (SCAN_INVALID / SCAN_NOT_FOUND / SCAN_INCOMPLETE / FETCH_FAILED /
  // ARCH_FAILED) while the technical message goes to the console only.
  function loaderError(code, message) {
    const err = new Error(message);
    err.code = code;
    return err;
  }

  async function fetchScan(scanID, visaToken) {
    const res = await fetch(QUERY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-visa-token': visaToken,
        'rid': 'anti-csrf',
      },
      body: JSON.stringify({
        operationName: 'getMeasurementResultQuery',
        variables: { scanID: scanID },
        query: QUERY,
      }),
    });
    if (!res.ok) throw loaderError('FETCH_FAILED', 'Volumental query failed (HTTP ' + res.status + ')');
    const json = await res.json();
    if (json.errors && json.errors.length) {
      throw loaderError('SCAN_INVALID', 'Volumental query error: ' + json.errors[0].message);
    }
    return json.data ? json.data.scan : null;
  }

  // Faithful port of the backend's low-arch correction (ms-rig-pointcloud
  // Job.py:582-608, addLowArchOffset), applied so web arch values get the SAME
  // post-processing as the values the backend stores for the email (decision D19,
  // 2026-07-24 — consistency-first while Finding 2 in FINDINGS-arch-measurement.md
  // is still open with the arch author; if the backend ever stops applying the
  // correction, delete this to match). Quirks preserved deliberately: ratios exactly
  // 0.01, or >= 0.03, pass through unchanged.
  function applyLowArchCorrection(archHeightMm, footLengthMm) {
    const normalized = archHeightMm / footLengthMm;
    if (normalized < 0.01) return 0.023 * footLengthMm;
    if (normalized > 0.01 && normalized < 0.03) {
      const factor = ((0.03 - normalized) / 0.02) * 0.013;
      return (normalized + factor) * footLengthMm;
    }
    return archHeightMm;
  }

  // Build the mm measurements object computeFootFacts expects, from a Volumental
  // measurement (metres) plus the two arch-extraction results (already mm). Shared with
  // volumental-widget.liquid's post-scan path so the assembly logic exists exactly once.
  // Arch heights pass through the backend-mirroring low-arch correction above; the
  // correction ratio uses the same (Volumental-derived) foot length fed downstream.
  function assemble(measurement, leftArch, rightArch) {
    const leftLengthMm = measurement.leftLength * 1000;
    const rightLengthMm = measurement.rightLength * 1000;
    return {
      leftFootLength: leftLengthMm,
      leftFootWidth: measurement.leftWidth * 1000,
      leftFootArchHeight: applyLowArchCorrection(leftArch.archHeight, leftLengthMm),
      rightFootLength: rightLengthMm,
      rightFootWidth: measurement.rightWidth * 1000,
      rightFootArchHeight: applyLowArchCorrection(rightArch.archHeight, rightLengthMm),
    };
  }

  // Resolve a scan from either a full internal id or a bare publicID (see @file).
  async function resolveScan(idOrPublicID, visaToken) {
    if (idOrPublicID.indexOf('-') === -1) {
      // No dashes → already an internal (base64) id.
      return fetchScan(idOrPublicID, visaToken);
    }
    let lastError = null;
    for (const scanType of SCAN_TYPES) {
      try {
        const scan = await fetchScan(encodeScanID(idOrPublicID, scanType), visaToken);
        if (scan) return scan;
      } catch (err) {
        lastError = err;
      }
    }
    if (lastError) throw lastError;
    return null;
  }

  /**
   * Fetch a scan and compute its footFacts.
   *
   * @param {{ scanId: string, visaToken?: string }} options
   * @returns {Promise<{ footFacts: object, measurements: object,
   *                     scan: { id, publicID, createdAt },
   *                     arch: { left: object, right: object } }>}
   * @throws on missing/expired scans, fetch failures, or arch-extraction failures —
   *         callers own the loading/error UI.
   */
  async function load(options) {
    const opts = options || {};
    if (!opts.scanId) throw new Error('FootFactsLoader.load: scanId is required');
    if (!window.FootFactsCalculator) {
      throw new Error('FootFactsLoader.load: foot-facts-calculator.js must be loaded first');
    }
    if (!window.ArchHeightCalculator) {
      throw new Error('FootFactsLoader.load: arch-height-calculator.js must be loaded first');
    }

    const visaToken = opts.visaToken || DEFAULT_VISA_TOKEN;
    const scan = await resolveScan(String(opts.scanId).trim(), visaToken);
    if (!scan) throw loaderError('SCAN_NOT_FOUND', 'Scan not found — the id may be wrong or the scan expired');
    if (!scan.measurement) throw loaderError('SCAN_INCOMPLETE', 'Scan has no measurement data');
    if (!scan.meshes || !scan.meshes.leftFoot || !scan.meshes.rightFoot) {
      throw loaderError('SCAN_INCOMPLETE', 'Scan has no mesh data (required for arch height)');
    }

    const [leftArch, rightArch] = await Promise.all([
      window.ArchHeightCalculator.fromUrl(scan.meshes.leftFoot, 'left'),
      window.ArchHeightCalculator.fromUrl(scan.meshes.rightFoot, 'right'),
    ]);
    if (leftArch.error) throw loaderError('ARCH_FAILED', 'Left arch extraction failed: ' + leftArch.error);
    if (rightArch.error) throw loaderError('ARCH_FAILED', 'Right arch extraction failed: ' + rightArch.error);

    const measurements = assemble(scan.measurement, leftArch, rightArch);
    const footFacts = window.FootFactsCalculator.computeFootFacts({ measurements });

    return {
      footFacts,
      measurements,
      scan: { id: scan.id, publicID: scan.publicID, createdAt: scan.createdAt },
      arch: { left: leftArch, right: rightArch },
    };
  }

  window.FootFactsLoader = {
    load,
    assemble,
    applyLowArchCorrection,
    resolveScan,
    encodeScanID,
    DEFAULT_VISA_TOKEN,
  };
})();
